import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { getAtalhos, salvarAtalhos } from '../services/api';

/**
 * Atalhos da barra do topo, por usuário.
 *
 * Vive num contexto porque duas telas mexem no mesmo estado: a sidebar marca e
 * desmarca (ícone de bandeira) e a navbar exibe. O backend é a fonte da verdade
 * e já devolve a lista filtrada pelas permissões do usuário; aqui só refletimos.
 */
const AtalhosContext = createContext({
  atalhos: [],
  max: 5,
  carregando: true,
  temAtalho: () => false,
  alternarAtalho: () => {},
  removerAtalho: () => {},
  cheio: false,
});

export const AtalhosProvider = ({ children }) => {
  const [atalhos, setAtalhos] = useState([]);
  const [max, setMax] = useState(8);
  const [carregando, setCarregando] = useState(true);
  // Páginas com gravação em andamento: quem disparou a ação troca o próprio
  // ícone por um spinner até o backend confirmar.
  const [pendentes, setPendentes] = useState(() => new Set());
  const [reordenando, setReordenando] = useState(false);

  const marcar = useCallback((pagina, ativo) => {
    setPendentes((atual) => {
      const proximo = new Set(atual);
      if (ativo) proximo.add(pagina); else proximo.delete(pagina);
      return proximo;
    });
  }, []);

  const carregar = useCallback(async () => {
    // Páginas públicas (responder cotação, solicitação por token) não têm
    // token. Chamar /atalhos ali devolve 401 e o interceptor do api.js
    // redireciona para /login, expulsando o fornecedor da página dele.
    if (!window.localStorage.getItem('token')) {
      setCarregando(false);
      return;
    }

    const res = await getAtalhos();
    if (res) {
      setAtalhos(res.atalhos || []);
      if (res.max) setMax(res.max);
    }
    setCarregando(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  /** Grava otimista: a barra responde na hora e volta atrás se a API recusar. */
  const persistir = useCallback(async (novos) => {
    const anteriores = atalhos;
    setAtalhos(novos);

    const res = await salvarAtalhos(novos.map((a) => a.pagina));
    if (res) {
      setAtalhos(res.atalhos || []);
      if (res.max) setMax(res.max);
    } else {
      setAtalhos(anteriores);
    }
  }, [atalhos]);

  const paginas = useMemo(() => new Set(atalhos.map((a) => a.pagina)), [atalhos]);
  const temAtalho = useCallback((pagina) => paginas.has(pagina), [paginas]);

  const alternarAtalho = useCallback(async (pagina, label) => {
    if (pendentes.has(pagina)) return;

    let novos;
    if (paginas.has(pagina)) {
      novos = atalhos.filter((a) => a.pagina !== pagina);
    } else {
      if (atalhos.length >= max) return;
      novos = [...atalhos, { pagina, label }];
    }

    marcar(pagina, true);
    try { await persistir(novos); } finally { marcar(pagina, false); }
  }, [atalhos, max, paginas, pendentes, persistir, marcar]);

  const removerAtalho = useCallback(async (pagina) => {
    if (pendentes.has(pagina)) return;

    marcar(pagina, true);
    try { await persistir(atalhos.filter((a) => a.pagina !== pagina)); }
    finally { marcar(pagina, false); }
  }, [atalhos, pendentes, persistir, marcar]);

  /** Move um atalho de posição (arrastar e soltar na navbar). */
  const reordenarAtalhos = useCallback(async (de, para) => {
    if (de === para || de < 0 || para < 0 || de >= atalhos.length || para >= atalhos.length) return;

    const novos = [...atalhos];
    const [movido] = novos.splice(de, 1);
    novos.splice(para, 0, movido);

    setReordenando(true);
    try { await persistir(novos); } finally { setReordenando(false); }
  }, [atalhos, persistir]);

  const salvando = useCallback((pagina) => pendentes.has(pagina), [pendentes]);

  const valor = useMemo(() => ({
    atalhos,
    max,
    carregando,
    temAtalho,
    alternarAtalho,
    removerAtalho,
    reordenarAtalhos,
    salvando,
    reordenando,
    cheio: atalhos.length >= max,
  }), [atalhos, max, carregando, temAtalho, alternarAtalho, removerAtalho,
       reordenarAtalhos, salvando, reordenando]);

  return <AtalhosContext.Provider value={valor}>{children}</AtalhosContext.Provider>;
};

export const useAtalhos = () => useContext(AtalhosContext);
